import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, ALTCHA_CHALLENGE_URL, ALTCHA_ENABLED } from '../config';
import Footer from '../components/Footer';
import 'altcha';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [altchaPayload, setAltchaPayload] = useState(null);
  const [altchaError, setAltchaError] = useState(null);
  const altchaRef = useRef(null);
  const navigate = useNavigate();

  // Handle ALTCHA widget events
  useEffect(() => {
    const widget = altchaRef.current;
    if (!widget) return;

    const handleStateChange = (ev) => {
      if (ev.detail) {
        if (ev.detail.state === 'verified') {
          setAltchaPayload(ev.detail.payload);
          setAltchaError(null);
        } else if (ev.detail.state === 'error') {
          setAltchaPayload(null);
          setAltchaError('Errore verifica CAPTCHA. Riprova.');
        } else if (ev.detail.state === 'unverified') {
          setAltchaPayload(null);
        }
      }
    };

    widget.addEventListener('statechange', handleStateChange);
    return () => widget.removeEventListener('statechange', handleStateChange);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Validate ALTCHA (only if enabled)
    if (ALTCHA_ENABLED && !altchaPayload) {
      setError('Completa la verifica CAPTCHA');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, altcha_payload: altchaPayload }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send reset email');
      }

      const data = await response.json();
      setMessage(data.message);
      setIsSuccess(true);
    } catch (err) {
      setError(err.message);
      // Reset ALTCHA widget on error
      setAltchaPayload(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col p-4">
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Password dimenticata</h2>

        {isSuccess ? (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {message}
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {message && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
                {message}
              </div>
            )}

            <p className="text-sm text-gray-600 mb-6 text-center">
              Inserisci il tuo indirizzo email e ti invieremo un link per reimpostare la password. Se non la ricevi, controlla la cartella spam/indesiderata.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* ALTCHA Widget - Self-hosted CAPTCHA */}
              {ALTCHA_ENABLED && ALTCHA_CHALLENGE_URL && (
                <div className="flex flex-col items-center gap-2">
                  <altcha-widget
                    ref={altchaRef}
                    challengeurl={ALTCHA_CHALLENGE_URL}
                    hidefooter
                    strings={JSON.stringify({
                      label: "Non sono un robot",
                      verifying: "Verifica in corso...",
                      verified: "Verificato",
                      error: "Verifica fallita. Riprova.",
                      expired: "Verifica scaduta. Riprova.",
                      waitAlert: "Verifica in corso... attendere."
                    })}
                  />
                  {altchaError && (
                    <p className="text-red-500 text-sm">{altchaError}</p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full text-white py-2 rounded-lg transition font-medium"
                style={{ backgroundColor: isLoading ? '#93C5FD' : '#0E60C6' }}
                onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0B4FA3')}
                onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0E60C6')}
              >
                {isLoading ? 'Invio in corso...' : 'Invia link di reset'}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Torna al login
          </button>
        </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}